import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectUser } from "../../store/authSlice";
import { isAdminUser } from "../../utils/userUtils";

// Asset previews
import Assessment from "../../assets/assessment-home.png";
import Home from "../../assets/Home-Main.png";
import AdminAnalytics from "../../assets/Admin.png";
import AdminUserManagement from "../../assets/Admin-User-Management.png";
import Chat from "../../assets/chat-home.png";
import Mindfulness from "../../assets/mindfulness-home.png";
import Live from "../../assets/live-home.png";
import Course from "../../assets/courses-home.png";
import Profile from "../../assets/Profile management.png";

import "./TourContainer.css";

const TOUR_VIDEOS = {
  mindsherpa:
    "https://storage.googleapis.com/mindsherpa-app-bucket/Videos/Mindsherpa.mp4",
  adminPortal:
    "https://storage.googleapis.com/mindsherpa-app-bucket/Videos/admin-portal.mp4",
  assessment:
    "https://storage.googleapis.com/mindsherpa-app-bucket/Videos/assessment-user.mp4",
  course:
    "https://storage.googleapis.com/mindsherpa-app-bucket/Videos/course-user.mp4",
  getStarted:
    "https://storage.googleapis.com/mindsherpa-app-bucket/Videos/get-started-user.mp4",
  liveSession:
    "https://storage.googleapis.com/mindsherpa-app-bucket/Videos/Live-session.mp4",
  mindfulness:
    "https://storage.googleapis.com/mindsherpa-app-bucket/Videos/Mindfulness-user.mp4",
  sherpa:
    "https://storage.googleapis.com/mindsherpa-app-bucket/Videos/Sherpa-user.mp4",
  profile:
    "https://storage.googleapis.com/mindsherpa-app-bucket/Videos/welcome-and-profile-user.mp4",
  dailyCheckin:
    "https://storage.googleapis.com/mindsherpa-app-bucket/Videos/checkin/check_ins_tour_mindsherpa_v1%20(720p).mp4",
};

export const TourContainer = () => {
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const userId = user?.id?.toString() || "defaultUser";
  const [isAdmin, setIsAdmin] = useState(false);

  const [stepIndex, setStepIndex] = useState(0);
  const [completedSteps, setCompletedSteps] = useState(0);
  const [opacity, setOpacity] = useState(1);
  const [adminSlide, setAdminSlide] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [mediaView, setMediaView] = useState("video"); // 'video' | 'image'

  const STORAGE_KEY = `tourProgress:${userId}`;
  const PENDING_KEY = `tourPending:${userId}`;
  const LAST_SEEN_KEY = `tourLast:${userId}`;

  const [completions, setCompletions] = useState({});

  useEffect(() => {
    if (isAdminUser(user)) {
      setIsAdmin(true);
    } else {
      setIsAdmin(false);
    }
  }, [user]);

  const loadCompletions = useCallback(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : {};
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      return {};
    }
  }, [STORAGE_KEY]);

  const saveCompletions = useCallback(
    (next) => {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    },
    [STORAGE_KEY]
  );

  const markCompleted = useCallback(
    (section) => {
      setCompletions((prev) => {
        if (prev[section]) return prev;
        const updated = { ...prev, [section]: true };
        saveCompletions(updated);
        return updated;
      });
    },
    [saveCompletions]
  );

  const isCompleted = useCallback(
    (section) => !!completions[section],
    [completions]
  );

  const tourSections = useMemo(
    () => ({
      welcome: {
        title: "Welcome, Ready to explore Mindsherpa?",
        content: "",
        label: "Next",
        url: "/tour",
        image: Home,
        video: TOUR_VIDEOS.mindsherpa,
      },
      profile: {
        title: "👤 Manage Your Profile",
        content:
          "The user profile is your central hub for personalization and control on our platform. It's where you can showcase your identity, manage your privacy settings, and customize how you interact with others. Think of it as your personal space, designed to make your experience uniquely yours.",
        label: "Go to Profile",
        url: "/profile",
        image: Profile,
        video: TOUR_VIDEOS.profile,
      },
      getstarted: {
        title: "🚀 Get Started",
        content:
          "Here's a quick orientation before you dive in. We'll help you set up essentials so your experience feels personal and smooth.",
        label: "Next",
        url: "/tour",
        image: Home,
        video: TOUR_VIDEOS.getStarted,
      },
      daily_checkin: {
        title: "🧩 Daily Check-in",
        content:
          "The check-in methodology uses three distinct daily 3-minute moments—Morning Focus, After Work Growth, and Nightly Reflection—to encourage you to pause, reflect, and recharge. This structured approach uses intention setting and well-being scoring to foster growth, anchor you to the present moment, and maximize performance. Each check-in is followed by guidance to reinforce positive mental practices, with your statistics page offering personalized insights for continuous improvement.",
        label: "Begin Check-ins",
        url: "/home",
        image: Home,
        video: TOUR_VIDEOS.dailyCheckin,
      },
      assessment: {
        title: "📊 Know Where You Stand, And Grow from There.",
        content:
          "Mindsherpa’s confidential self-assessment tools help users check in on their mental well-being with science-based surveys and personalized insights. Track progress over time and get tailored recommendations to support your emotional and psychological health. No judgment, just growth.",
        label: "Unlock Your Report",
        url: "/assessment-dashboard",
        image: Assessment,
        video: TOUR_VIDEOS.assessment,
      },
      chat: {
        title: "💬 Meet Your Mindful Companion. Anytime, Anywhere.",
        content:
          "Mindsherpa’s AI-powered companion offers 24/7 support for stress, anxiety, focus, and emotional well-being. Backed by professional guidance and always confidential, it’s like having a mental well-being coach in your pocket, ready when you need it most.",
        label: "Talk to Sherpa",
        url: "/sherpa-dashboard",
        image: Chat,
        video: TOUR_VIDEOS.sherpa,
      },
      mindfulness: {
        title: "🧘 Science-Backed Mindfulness, Made Practical.",
        content:
          "Mindsherpa delivers expertly curated content to help employees build resilience, manage stress, and stay grounded, right from their phones. From quick breathing exercises to in-depth guides on sleep and emotional balance, every piece is designed for real-world impact in just minutes a day.",
        label: "Begin Practice",
        url: "/mindfulness",
        image: Mindfulness,
        video: TOUR_VIDEOS.mindfulness,
      },
      live: {
        title: "🎤 Wellness That Connects.",
        content:
          "Mindsherpa’s live group sessions bring employees together for expert-led workshops on mindfulness, stress, focus, and more. These interactive experiences build a shared culture of well-being while giving teams practical tools they can use every day. No pressure, just presence.",
        label: "Reserve Your Spot",
        url: "/live",
        image: Live,
        video: TOUR_VIDEOS.liveSession,
      },
      courses: {
        title: "🎓 Learn More From Experts",
        content:
          "Our courses are designed to be an effective blend of compelling lectures and personal coaching. This approach helps you not only learn new information but also apply it to your life and see real results. Our mentors are carefully chosen experts who are not only knowledgeable but also passionate, relatable, and genuinely committed to helping others.",
        label: "View Courses",
        url: "/courses",
        image: Course,
        video: TOUR_VIDEOS.course,
      },
    }),
    []
  );

  const adminSlides = useMemo(
    () => [
      {
        title: "👥 Administrator User Management",
        content:
          "Manage Your Employees. Explore how easy it is to add, update and view employee information. Getting started is easy. Begin by inviting your team members to join Mindsherpa so they can access guided mindfulness sessions, curated content, assessments, and live group experiences designed to support their mental and emotional wellness.",
        image: AdminUserManagement,
        video: TOUR_VIDEOS.adminPortal,
        label: "Manage Users",
        url: "/admin/usermanagement",
      },
      {
        title: "📊 Analytics Dashboard",
        content:
          "Actionable Insights at Your Fingertips: Gain immediate access to key workforce data through pre-built and custom reports. Measure adoption, engagement, and well-being trends with privacy-first reporting.",
        image: AdminAnalytics,
        video: TOUR_VIDEOS.adminPortal,
        label: "View Analytics",
        url: "/admin/analytics",
      },
    ],
    []
  );

  // Stepper Sequence
  const sequence = useMemo(() => {
    return isAdmin
      ? [
          "welcome",
          "getstarted",
          "daily_checkin",
          "assessment",
          "chat",
          "mindfulness",
          "live",
          "courses",
          "profile",
          "admin",
        ]
      : [
          "welcome",
          "getstarted",
          "daily_checkin",
          "assessment",
          "chat",
          "mindfulness",
          "live",
          "courses",
          "profile",
        ];
  }, [isAdmin]);

  const activeSection = sequence[stepIndex] || "welcome";
  const displaySection = activeSection;
  const isLastStep = stepIndex >= sequence.length - 1;
  const progressPercent = Math.min(100, Math.round(((stepIndex + 1) / sequence.length) * 100));

  const labelMap = {
    welcome: "Welcome",
    getstarted: "Get Started",
    daily_checkin: "Daily Check-in",
    assessment: "Assessment",
    chat: "Sherpa",
    mindfulness: "Mindfulness",
    live: "Live",
    courses: "Courses",
    profile: "Manage Profile",
    admin: "Admin Portal",
  };

  useEffect(() => {
    const last = localStorage.getItem(LAST_SEEN_KEY);
    if (last && sequence.includes(last)) {
      setStepIndex(sequence.indexOf(last));
      return;
    }
    const savedPercent = localStorage.getItem(userId);
    if (savedPercent) {
      try {
        const percent = JSON.parse(savedPercent);
        if (typeof percent === "number" && percent >= 0 && percent <= 100) {
          const completed = Math.round((percent / 100) * sequence.length);
          setCompletedSteps(completed);
          setStepIndex(Math.min(completed, sequence.length - 1));
        }
      } catch (e) {
        console.error("Error parsing tourProgress", e);
      }
    }
  }, [sequence, userId, LAST_SEEN_KEY]);

  useEffect(() => {
    const stored = loadCompletions();
    const pending = localStorage.getItem(PENDING_KEY);
    if (pending) {
      stored[pending] = true;
      localStorage.removeItem(PENDING_KEY);
      saveCompletions(stored);
    }
    setCompletions(stored);
  }, [loadCompletions, saveCompletions, PENDING_KEY]);

  useEffect(() => {
    const count = sequence.reduce(
      (acc, s) => acc + (completions[s] ? 1 : 0),
      0
    );
    setCompletedSteps(count);
  }, [completions, sequence]);

  useEffect(() => {
    const percent = (completedSteps / sequence.length) * 100;
    localStorage.setItem(userId, JSON.stringify(percent));
  }, [completedSteps, sequence.length, userId]);

  const handleSectionClick = (section) => {
    const index = sequence.indexOf(section);
    if (index === -1 || index === stepIndex) return;
    localStorage.setItem(LAST_SEEN_KEY, section);
    setOpacity(0);
    setTimeout(() => {
      setStepIndex(index);
      setExpanded(false);
      setMediaView("video");
      setOpacity(1);
    }, 200);
  };

  const goToNext = () => {
    const current = sequence[stepIndex];
    if (current === "welcome" || current === "getstarted") {
      markCompleted(current);
    }

    if (!isLastStep) {
      const nextSection = sequence[stepIndex + 1];
      localStorage.setItem(LAST_SEEN_KEY, nextSection);
      handleSectionClick(nextSection);
    } else {
      // Completed last step!
      markCompleted(current);
      navigate("/home");
    }
  };

  const goToPrevious = () => {
    if (stepIndex > 0) {
      const prevSection = sequence[stepIndex - 1];
      localStorage.setItem(LAST_SEEN_KEY, prevSection);
      handleSectionClick(prevSection);
    }
  };

  const navigateSafe = (to) => {
    navigate(to, { replace: false });
  };

  const handleCTA = () => {
    const current = sequence[stepIndex];
    localStorage.setItem(LAST_SEEN_KEY, current);

    if (current === "admin") {
      localStorage.setItem(PENDING_KEY, "admin");
      const target = adminSlides[adminSlide].url;
      setTimeout(() => navigateSafe(target), 0);
      return;
    }

    if (current !== "welcome" && current !== "getstarted") {
      localStorage.setItem(PENDING_KEY, current);
    }

    const target = tourSections[current].url;
    setTimeout(() => navigateSafe(target), 0);
  };

  // Determine current active content
  const currentContent = useMemo(() => {
    if (displaySection === "admin") {
      return {
        title: adminSlides[adminSlide].title,
        content: adminSlides[adminSlide].content,
        video: adminSlides[adminSlide].video,
        image: adminSlides[adminSlide].image,
        label: adminSlides[adminSlide].label,
      };
    }
    return {
      title:
        displaySection === "welcome"
          ? isAdmin
            ? tourSections.welcome.title
            : "Welcome to Your Well-being Journey"
          : tourSections[displaySection]?.title || "",
      content: tourSections[displaySection]?.content || "",
      video: tourSections[displaySection]?.video || "",
      image: tourSections[displaySection]?.image || null,
      label: tourSections[displaySection]?.label || "Explore",
    };
  }, [displaySection, adminSlide, isAdmin, tourSections, adminSlides]);

  return (
    <div id="tour-container" className="tour-inner-content">
      {/* Header Area */}
        <div className="tour-header">
          <div className="tour-badge-pill">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <circle cx="12" cy="12" r="10" />
              <polygon points="12 6 12 12 16 14" />
            </svg>
            Interactive Platform Tour
          </div>
          <h1 className="tour-title">
            {isAdmin
              ? "Take your first steps with our platform"
              : "Your Well-being Journey"}
          </h1>
          <p className="tour-subtitle">
            {isAdmin
              ? "As a leader for your organization, you play a key role in shaping your journey toward greater well-being and mindful engagement. Explore our core tools to build a healthier, more connected workplace."
              : "Explore our key features to get started on your personal well-being journey."}
          </p>
        </div>

        {/* Progress Bar (Overall) */}
        <div className="tour-progress-bar-container">
          <div className="tour-progress-bar-track">
            <div
              className="tour-progress-bar-fill"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <div className="tour-progress-bar-meta">
            <span>
              Step {stepIndex + 1} of {sequence.length}:{" "}
              <strong>{labelMap[activeSection]}</strong>
            </span>
            <span>{progressPercent}% Complete</span>
          </div>
        </div>

        {/* Mobile Stepper Bar (Horizontal Chip Scroll) */}
        <div className="tour-mobile-stepper">
          {sequence.map((key, idx) => {
            const isActive = activeSection === key;
            const done = isCompleted(key);
            return (
              <button
                key={key}
                type="button"
                className={`tour-mobile-chip ${isActive ? "active" : ""} ${done ? "done" : ""}`}
                onClick={() => handleSectionClick(key)}
              >
                {done ? (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                ) : (
                  <span>{idx + 1}</span>
                )}
                <span>{labelMap[key]}</span>
              </button>
            );
          })}
        </div>

        {/* Main 2-Column Layout */}
        <div className="tour-layout">
          {/* Left Vertical Stepper (Desktop) */}
          <aside className="tour-stepper-sidebar" aria-label="Tour navigation steps">
            <div className="tour-stepper-header">
              <h3>Tour Checklist</h3>
              <span className="tour-stepper-count">
                {completedSteps} / {sequence.length}
              </span>
            </div>

            <nav className="tour-stepper-list">
              {sequence.map((key, idx) => {
                const isActive = activeSection === key;
                const done = isCompleted(key);
                const isSubStep =
                  key === "daily_checkin" ||
                  key === "assessment" ||
                  key === "chat" ||
                  key === "mindfulness" ||
                  key === "live" ||
                  key === "courses";

                return (
                  <div
                    key={key}
                    role="button"
                    tabIndex={0}
                    className={`tour-step-item ${isActive ? "is-active" : ""} ${done ? "is-done" : ""} ${isSubStep ? "is-substep" : ""}`}
                    onClick={() => handleSectionClick(key)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleSectionClick(key);
                      }
                    }}
                  >
                    <div
                      className={`tour-step-marker ${
                        done ? "done" : isActive ? "active" : "pending"
                      }`}
                    >
                      {done ? (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      ) : (
                        idx + 1
                      )}
                    </div>
                    <span className="tour-step-label">{labelMap[key] || key}</span>
                  </div>
                );
              })}
            </nav>
          </aside>

          {/* Right Main Content Card */}
          <main className="tour-content-card" style={{ opacity }}>
            <div className="tour-card-header">
              <span className="tour-step-badge">
                Step {stepIndex + 1} of {sequence.length} &bull; {labelMap[displaySection]}
              </span>

              {/* Admin Slides Switcher */}
              {displaySection === "admin" && (
                <div className="admin-tabs-nav">
                  <button
                    type="button"
                    className={`admin-tab-btn ${adminSlide === 0 ? "active" : ""}`}
                    onClick={() => setAdminSlide(0)}
                  >
                    👥 User Management
                  </button>
                  <button
                    type="button"
                    className={`admin-tab-btn ${adminSlide === 1 ? "active" : ""}`}
                    onClick={() => setAdminSlide(1)}
                  >
                    📊 Analytics Dashboard
                  </button>
                </div>
              )}
            </div>

            <div className="tour-card-body-grid">
              {/* Left Narrative Column */}
              <div className="tour-narrative-col">
                <h2 className="tour-section-heading">{currentContent.title}</h2>

                <div
                  className={`tour-description-box ${
                    displaySection !== "getstarted" && !expanded
                      ? "is-collapsed"
                      : ""
                  }`}
                >
                  {displaySection === "welcome" ? (
                    isAdmin ? (
                      <>
                        <p>
                          Welcome to <strong>Mindsherpa</strong>! To get started, invite your team members to join your workplace wellness community.
                        </p>
                        <p>
                          Once they're on board, they can immediately explore guided mindfulness sessions, curated expert content, and live interactive workshops designed to reduce stress and support emotional well-being.
                        </p>
                        <p>
                          As an organization leader, you can easily invite employees, assign roles, and view engagement metrics directly from your central dashboard.
                        </p>
                      </>
                    ) : (
                      <>
                        <p>
                          Feeling stressed, unproductive, or burned out? <strong>Mindsherpa</strong> is here to help you discover a calmer, more focused version of yourself!
                        </p>
                        <p>
                          Now that you've completed onboarding, customize your personal experience. Feel free to enhance your privacy by using an <strong>avatar and display name</strong>.
                        </p>
                        <p>
                          Rest assured, your personal responses and assessment data are <strong>never shared with your employer</strong>—we only provide strictly anonymized aggregate insights.
                        </p>
                        <p>
                          Click <strong>"Next Step"</strong> below to explore your well-being roadmap!
                        </p>
                      </>
                    )
                  ) : displaySection === "getstarted" ? (
                    <>
                      <p>
                        Welcome to your daily well-being routine! Start with just <strong>3 to 5 minutes a day</strong> to see noticeable improvements in focus, calmness, and mood.
                      </p>
                      <p>
                        <strong>Recommended daily flow to build habits:</strong>
                      </p>
                      <ol>
                        <li>Start your morning with a 1-minute <strong>Morning Focus Check-in</strong>.</li>
                        <li>Take your comprehensive <strong>Monthly Assessment</strong> to track growth.</li>
                        <li>Ask <strong>Sherpa AI</strong> for personalized guidance anytime you feel stressed.</li>
                        <li>Practice a quick 3-minute <strong>Mindfulness Reset</strong> during midday.</li>
                        <li>Join our bi-weekly 30-minute <strong>Lunch & Learn</strong> live events.</li>
                      </ol>
                    </>
                  ) : (
                    <p>{currentContent.content}</p>
                  )}
                </div>

                {displaySection !== "getstarted" && (
                  <button
                    type="button"
                    className="tour-toggle-expand"
                    onClick={() => setExpanded(!expanded)}
                  >
                    {expanded ? "View Less ▲" : "View More ▼"}
                  </button>
                )}
              </div>

              {/* Right Media Player Column */}
              <div className="tour-media-col">
                <div className="tour-media-frame">
                  {mediaView === "video" && currentContent.video ? (
                    <video
                      key={currentContent.video + (displaySection === "admin" ? adminSlide : "")}
                      src={currentContent.video}
                      controls
                      playsInline
                      className="tour-video-player"
                    />
                  ) : currentContent.image ? (
                    <img
                      src={currentContent.image}
                      alt={currentContent.title}
                      className="tour-image-preview"
                    />
                  ) : (
                    <video
                      key={currentContent.video}
                      src={currentContent.video}
                      controls
                      playsInline
                      className="tour-video-player"
                    />
                  )}
                </div>

                {/* Optional Media Toggle if image is available */}
                {currentContent.image && currentContent.video && (
                  <div className="tour-media-switch">
                    <button
                      type="button"
                      className={`tour-switch-btn ${mediaView === "video" ? "active" : ""}`}
                      onClick={() => setMediaView("video")}
                    >
                      ▶ Video Guide
                    </button>
                    <button
                      type="button"
                      className={`tour-switch-btn ${mediaView === "image" ? "active" : ""}`}
                      onClick={() => setMediaView("image")}
                    >
                      🖼 Screenshot Preview
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Action Footer */}
            <div className="tour-actions-footer">
              <div>
                {displaySection !== "welcome" && displaySection !== "getstarted" && (
                  <button
                    type="button"
                    className="tour-cta-btn"
                    onClick={handleCTA}
                  >
                    <span>{currentContent.label}</span>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <line x1="5" y1="12" x2="19" y2="12" />
                      <polyline points="12 5 19 12 12 19" />
                    </svg>
                  </button>
                )}
              </div>

              <div className="tour-nav-buttons-group">
                <button
                  type="button"
                  className="tour-btn-back"
                  onClick={goToPrevious}
                  disabled={stepIndex === 0}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <line x1="19" y1="12" x2="5" y2="12" />
                    <polyline points="12 19 5 12 12 5" />
                  </svg>
                  Back
                </button>

                <button
                  type="button"
                  className="tour-btn-next"
                  onClick={goToNext}
                >
                  {isLastStep ? "Finish Tour ✓" : "Next Step"}
                  {!isLastStep && (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <line x1="5" y1="12" x2="19" y2="12" />
                      <polyline points="12 5 19 12 12 19" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </main>
        </div>
      </div>
  );
};
